import { prisma } from "@/lib/db";
import { DEFAULTS, MODE_CONFIG } from "./constants";
import { getActiveGroup } from "./group-manager";
import {
  calculatePCorrect,
  scoreCandidate,
  selectBestCandidate,
} from "./predictor";
import { createInitialState } from "./state-updater";
import type {
  CandidateState,
  PresentedType,
  ProblemStatus,
  ScoredCandidate,
  SessionItem,
  SessionMode,
  SessionPlan,
  SessionPlanItem,
} from "./types";

/**
 * Compute new problem budget based on review backlog
 */
function computeNewQuota(
  mode: SessionMode,
  sessionSize: number,
  reviewBacklog: number
): number {
  const baseNewFraction = MODE_CONFIG[mode].newFraction;

  if (reviewBacklog >= 0.8 * sessionSize) {
    return 0;
  } else if (reviewBacklog >= 0.5 * sessionSize) {
    return Math.floor(0.5 * baseNewFraction * sessionSize);
  }
  return Math.floor(baseNewFraction * sessionSize);
}

interface CandidateWithProblem {
  problemId: string;
  groupId: string;
  state: CandidateState;
}

/**
 * Load due review candidates
 */
async function loadDueReviewCandidates(
  userId: string,
  now: Date
): Promise<CandidateWithProblem[]> {
  const states = await prisma.userProblemState.findMany({
    where: {
      userId,
      dueAt: { lte: now },
      status: { notIn: ["new", "mastered"] },
    },
    include: {
      problem: true,
    },
  });

  return states.map((s) => ({
    problemId: s.problemId,
    groupId: s.problem.groupId,
    state: {
      status: s.status as ProblemStatus,
      memoryStrength: s.memoryStrength,
      difficulty: s.difficulty,
      consecutiveCorrect: s.consecutiveCorrect,
      consecutiveWrong: s.consecutiveWrong,
      lastResult: s.lastResult as "correct" | "wrong" | null,
      lastAnsweredAt: s.lastAnsweredAt,
      dueAt: s.dueAt,
      seenCountToday: s.seenCountToday,
      recentAvgResponseMs: s.recentAvgResponseMs,
      successCount: s.successCount,
    },
  }));
}

/**
 * Load retry candidates (recently wrong items)
 */
async function loadRetryCandidates(
  userId: string,
  now: Date
): Promise<CandidateWithProblem[]> {
  const fiveMinutesAgo = new Date(
    now.getTime() - DEFAULTS.retryDelayMinutes * 60 * 1000
  );

  const states = await prisma.userProblemState.findMany({
    where: {
      userId,
      lastResult: "wrong",
      lastAnsweredAt: { lte: fiveMinutesAgo },
      consecutiveWrong: { lte: 2 },
    },
    include: {
      problem: true,
    },
  });

  return states.map((s) => ({
    problemId: s.problemId,
    groupId: s.problem.groupId,
    state: {
      status: s.status as ProblemStatus,
      memoryStrength: s.memoryStrength,
      difficulty: s.difficulty,
      consecutiveCorrect: s.consecutiveCorrect,
      consecutiveWrong: s.consecutiveWrong,
      lastResult: s.lastResult as "correct" | "wrong" | null,
      lastAnsweredAt: s.lastAnsweredAt,
      dueAt: s.dueAt,
      seenCountToday: s.seenCountToday,
      recentAvgResponseMs: s.recentAvgResponseMs,
      successCount: s.successCount,
    },
  }));
}

/**
 * Load new candidates from active group
 */
async function loadNewCandidates(
  userId: string,
  groupId: string
): Promise<CandidateWithProblem[]> {
  // Get problems that don't have a state yet (new)
  const problems = await prisma.problem.findMany({
    where: {
      groupId,
      userProblemStates: {
        none: {
          userId,
        },
      },
    },
    orderBy: { sequenceInGroup: "asc" },
  });

  return problems.map((p) => ({
    problemId: p.id,
    groupId: p.groupId,
    state: {
      status: "new" as ProblemStatus,
      memoryStrength: 0,
      difficulty: 0.5,
      consecutiveCorrect: 0,
      consecutiveWrong: 0,
      lastResult: null,
      lastAnsweredAt: null,
      dueAt: null,
      seenCountToday: 0,
      recentAvgResponseMs: null,
      successCount: 0,
    },
  }));
}

/**
 * Load booster candidates (not due yet but moderately weak)
 */
async function loadBoosterCandidates(
  userId: string,
  now: Date
): Promise<CandidateWithProblem[]> {
  const states = await prisma.userProblemState.findMany({
    where: {
      userId,
      dueAt: { gt: now },
      status: { notIn: ["new", "mastered"] },
    },
    include: {
      problem: true,
    },
  });

  // Filter for moderately weak items (p_correct between 0.55 and 0.80)
  const candidates: CandidateWithProblem[] = [];

  for (const s of states) {
    const state: CandidateState = {
      status: s.status as ProblemStatus,
      memoryStrength: s.memoryStrength,
      difficulty: s.difficulty,
      consecutiveCorrect: s.consecutiveCorrect,
      consecutiveWrong: s.consecutiveWrong,
      lastResult: s.lastResult as "correct" | "wrong" | null,
      lastAnsweredAt: s.lastAnsweredAt,
      dueAt: s.dueAt,
      seenCountToday: s.seenCountToday,
      recentAvgResponseMs: s.recentAvgResponseMs,
      successCount: s.successCount,
    };

    const pCorrect = calculatePCorrect(state, now);

    if (pCorrect >= 0.55 && pCorrect <= 0.8) {
      candidates.push({
        problemId: s.problemId,
        groupId: s.problem.groupId,
        state,
      });
    }
  }

  return candidates;
}

/**
 * Build a study session
 */
export async function buildSession(
  userId: string,
  mode: SessionMode,
  sessionSize: number = DEFAULTS.sessionSize
): Promise<SessionPlan> {
  const now = new Date();
  const config = MODE_CONFIG[mode];

  // Reset today's seen count at start of new day
  await resetSeenCountIfNewDay(userId, now);

  // Get active group
  const activeGroup = await getActiveGroup(userId);

  if (!activeGroup) {
    throw new Error("No problem groups available");
  }

  // Load candidate pools
  const duePool = await loadDueReviewCandidates(userId, now);
  const retryPool = await loadRetryCandidates(userId, now);
  const newPool = await loadNewCandidates(userId, activeGroup.groupId);
  const boosterPool = await loadBoosterCandidates(userId, now);

  // Calculate quotas
  const reviewBacklog = duePool.length + retryPool.length;
  const newQuota = computeNewQuota(mode, sessionSize, reviewBacklog);
  let retryQuota = Math.min(
    retryPool.length,
    Math.ceil(DEFAULTS.retryFraction * sessionSize)
  );
  let boosterQuota = Math.ceil(DEFAULTS.boosterFraction * sessionSize);
  let reviewQuota = sessionSize - newQuota - retryQuota - boosterQuota;

  // Adjust quotas if not enough candidates
  if (reviewQuota < 0) {
    boosterQuota = Math.max(0, boosterQuota + reviewQuota);
    reviewQuota = sessionSize - newQuota - retryQuota - boosterQuota;
  }
  if (reviewQuota < 0) {
    retryQuota = Math.max(0, retryQuota + reviewQuota);
    reviewQuota = sessionSize - newQuota - retryQuota - boosterQuota;
  }

  const hardCap = Math.floor(config.maxHardFraction * sessionSize);
  let hardCount = 0;

  const queue: SessionItem[] = [];
  const counts = {
    review: 0,
    retry: 0,
    new: 0,
    booster: 0,
  };

  const usedProblemIds = new Set<string>();

  while (queue.length < sessionSize) {
    const previous = queue.length > 0 ? queue[queue.length - 1] : null;
    const previousGroupId = previous
      ? (await prisma.problem.findUnique({
          where: { id: previous.problemId },
          select: { groupId: true },
        }))?.groupId ?? null
      : null;

    let candidates: ScoredCandidate[] = [];

    // Score candidates from each pool based on remaining quotas
    if (counts.retry < retryQuota) {
      const scored = retryPool
        .filter((c) => !usedProblemIds.has(c.problemId))
        .map((c) =>
          scoreCandidate(
            c.problemId,
            c.groupId,
            c.state,
            mode,
            now,
            previousGroupId,
            "retry"
          )
        );
      candidates.push(...scored);
    }

    if (counts.review < reviewQuota) {
      const scored = duePool
        .filter((c) => !usedProblemIds.has(c.problemId))
        .map((c) =>
          scoreCandidate(
            c.problemId,
            c.groupId,
            c.state,
            mode,
            now,
            previousGroupId,
            "review"
          )
        );
      candidates.push(...scored);
    }

    if (counts.new < newQuota) {
      const scored = newPool
        .filter((c) => !usedProblemIds.has(c.problemId))
        .slice(0, 5) // Only consider first 5 new items
        .map((c) =>
          scoreCandidate(
            c.problemId,
            c.groupId,
            c.state,
            mode,
            now,
            previousGroupId,
            "new"
          )
        );
      candidates.push(...scored);
    }

    if (counts.booster < boosterQuota) {
      const scored = boosterPool
        .filter((c) => !usedProblemIds.has(c.problemId))
        .map((c) =>
          scoreCandidate(
            c.problemId,
            c.groupId,
            c.state,
            mode,
            now,
            previousGroupId,
            "booster"
          )
        );
      candidates.push(...scored);
    }

    // Filter out hard items if we've hit the cap
    candidates = candidates.filter((c) => {
      const isHard = c.pCorrect < config.minPCorrect;
      return !(isHard && hardCount >= hardCap);
    });

    if (candidates.length === 0) {
      break;
    }

    // Select best candidate
    const best = selectBestCandidate(queue, candidates, config.targetAccuracy);

    queue.push({
      problemId: best.problemId,
      type: best.poolType,
      pCorrect: best.pCorrect,
    });

    usedProblemIds.add(best.problemId);

    if (best.pCorrect < config.minPCorrect) {
      hardCount++;
    }

    counts[best.poolType]++;
  }

  // Create session in database
  const session = await prisma.studySession.create({
    data: {
      userId,
      mode,
      plannedSize: sessionSize,
      expectedAccuracy:
        queue.length > 0
          ? queue.reduce((sum, item) => sum + item.pCorrect, 0) / queue.length
          : 0,
    },
  });

  // Initialize new problems that will be introduced
  for (const item of queue) {
    if (item.type === "new") {
      const initialState = createInitialState(now);
      await prisma.userProblemState.upsert({
        where: {
          userId_problemId: {
            userId,
            problemId: item.problemId,
          },
        },
        update: {},
        create: {
          userId,
          problemId: item.problemId,
          status: initialState.status!,
          introducedAt: initialState.introducedAt,
          memoryStrength: initialState.memoryStrength!,
          difficulty: initialState.difficulty!,
          confidence: 0.2,
          dueAt: initialState.dueAt,
          successCount: 0,
          failureCount: 0,
          consecutiveCorrect: 0,
          consecutiveWrong: 0,
          seenCountToday: 0,
        },
      });
    }
  }

  // Update group introduced count
  const newCount = queue.filter((item) => item.type === "new").length;
  if (newCount > 0) {
    await prisma.userGroupState.update({
      where: {
        userId_groupId: {
          userId,
          groupId: activeGroup.groupId,
        },
      },
      data: {
        introducedCount: {
          increment: newCount,
        },
      },
    });
  }

  // Hydrate queue items with problem details
  const items: SessionPlanItem[] = [];
  for (let i = 0; i < queue.length; i++) {
    const item = queue[i];
    const problem = await prisma.problem.findUnique({
      where: { id: item.problemId },
    });

    if (problem) {
      items.push({
        problemId: item.problemId,
        problemText: problem.problemText,
        groupId: problem.groupId,
        type: item.type,
        pCorrect: item.pCorrect,
        position: i,
      });
    }
  }

  return {
    sessionId: session.id,
    expectedAccuracy: session.expectedAccuracy,
    items,
  };
}

/**
 * Reset seen count if it's a new day
 */
async function resetSeenCountIfNewDay(userId: string, now: Date): Promise<void> {
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);

  // Reset seen count for items last seen before today
  await prisma.userProblemState.updateMany({
    where: {
      userId,
      lastSeenAt: { lt: startOfToday },
      seenCountToday: { gt: 0 },
    },
    data: {
      seenCountToday: 0,
    },
  });
}

/**
 * Get the next problem in the current session based on dynamic adaptation
 */
export async function getNextProblem(
  userId: string,
  sessionId: string,
  recentResults: boolean[]
): Promise<SessionPlanItem | null> {
  const session = await prisma.studySession.findUnique({
    where: { id: sessionId },
  });

  if (!session) return null;

  const now = new Date();
  const mode = session.mode as SessionMode;
  const config = MODE_CONFIG[mode];

  // Calculate recent accuracy for dynamic adjustment
  const recentWindow = recentResults.slice(-5);
  const recentAccuracy =
    recentWindow.length > 0
      ? recentWindow.filter((r) => r).length / recentWindow.length
      : config.targetAccuracy;

  // Check if user is struggling (missed 2 in a row)
  const lastTwo = recentResults.slice(-2);
  const isStruggling =
    lastTwo.length >= 2 && lastTwo.every((r) => !r);

  // Adjust target based on performance
  let adjustedTarget = config.targetAccuracy;
  if (recentAccuracy <= config.targetAccuracy - 0.25) {
    adjustedTarget = Math.min(0.92, config.targetAccuracy + 0.08);
  } else if (recentAccuracy >= config.targetAccuracy + 0.12) {
    adjustedTarget = Math.max(0.6, config.targetAccuracy - 0.05);
  }

  // Load candidates
  const candidates = await loadDueReviewCandidates(userId, now);
  const newCandidates = await loadNewCandidates(
    userId,
    (await getActiveGroup(userId))?.groupId ?? ""
  );

  // If struggling, prefer easier items
  let pool = [...candidates, ...newCandidates];
  if (isStruggling) {
    pool = pool.filter((c) => {
      const pCorrect = calculatePCorrect(c.state, now);
      return pCorrect >= adjustedTarget;
    });
  }

  if (pool.length === 0) return null;

  // Score and select
  const scored = pool.map((c) =>
    scoreCandidate(
      c.problemId,
      c.groupId,
      c.state,
      mode,
      now,
      null,
      c.state.status === "new" ? "new" : "review"
    )
  );

  const best = selectBestCandidate([], scored, adjustedTarget);

  const problem = await prisma.problem.findUnique({
    where: { id: best.problemId },
  });

  if (!problem) return null;

  return {
    problemId: best.problemId,
    problemText: problem.problemText,
    groupId: problem.groupId,
    type: best.poolType,
    pCorrect: best.pCorrect,
    position: session.completedCount,
  };
}
